import React, { useRef, useState } from 'react'
import { Form, Overlay, Popover, Table } from 'react-bootstrap'
import { browserName } from 'react-device-detect'
import { Link } from 'react-router-dom'

import classNames from 'classnames'

import Badge from '../../components/_atoms/Badge/Badge'
import CategoryIcon from '../../components/_atoms/CategogyIcon/CategoryIcon'
import CopyButton from '../../components/_atoms/CopyButton/CopyButton'
import ListSkeleton from '../../components/_atoms/ListSkeleton/ListSkeleton'
import TableHeadPartSearch from '../../components/_atoms/TableHeadPartSearch/TableHeadPartSearch'
import TableHeadRangeFilter from '../../components/_atoms/TableHeadRangeFilter/TableHeadRangeFilter'
import ColumnSort from '../../components/_molecules/ColumnSort/ColumnSort'
import ResetFilter from '../../components/_molecules/ResetFilter/ResetFilter'
import TableHeadCategorySearchParts from '../../components/_molecules/TableHeadCategorySearchParts/TableHeadCategorySearchParts'
import TableHeadMarkSearchParts from '../../components/_molecules/TableHeadMarkSearchParts/TableHeadMarkSearchParts'
import TableHeadModelSearchParts from '../../components/_molecules/TableHeadModelSearchParts/TableHeadModelSearchParts'
import TableHeadPartNumberSearchParts from '../../components/_molecules/TableHeadPartNumberSearchParts/TableHeadPartNumberSearchParts'
import CellActionsParts from '../../components/_orgamisms/CellActionsParts/CellActionsParts'
import CellThumbCarousel from '../../components/_orgamisms/CellThumbCarousel/CellThumbCarousel'
import { Column } from '../../modules/store/partList/partListReducer'
import { storeDispatchTypes } from '../../modules/store/storeDispatchTypes'
import { SortFieldParts, SortIconType } from '../../types/common/commonTypes'
import { Part } from '../../types/part/partTypes'
import { countDaysInStock, formatDate, num_word } from '../../utils/converters'
import { colWidth } from '../../utils/stylePatches'

import styles from './styles.module.scss'

interface Props {
    columns: Column[]
    parts: Part[]
    isLoading: boolean
    selectAll: boolean
    selectedParts: Part[]
    partName?: string
    filterVendorCode?: string
    carFilter?: {
        markTitle: string
        modelTitle: string
    }
    priceFrom?: number
    priceTo?: number
    handleAllCheckboxChange: () => void
    handleCheckboxChange: (part: Part) => void
    openPartModal: (part: Part) => void
}

const TableSoldPartList = ({
    columns,
    parts,
    isLoading,
    selectAll,
    selectedParts,
    partName,
    filterVendorCode,
    carFilter,
    priceFrom,
    priceTo,
    handleAllCheckboxChange,
    handleCheckboxChange,
    openPartModal,
}: Props) => {
    const [showDealsPopupIdx, setShowDealsPopupIdx] = useState<number | null>(null)
    const [popupTarget, setPopupTarget] = useState<HTMLElement | null>(null)
    const popupTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    const partSoldQtyAmountCost = (part: Part) => {
        const qtyTotalAmountCost = (part.extraData?.soldQtyArr || []).reduce(
            (total, current) => total + Number(current?.price || 0),
            0,
        )

        return part.isSold ? (part.price || 0) + qtyTotalAmountCost : qtyTotalAmountCost || part.price
    }

    const partSoldQtyCount = (part: Part) => {
        const soldQtyArrCount = (part.extraData?.soldQtyArr || []).length
        return part.isSold ? 1 + soldQtyArrCount : soldQtyArrCount || 1
    }

    const handleQtyMouseEnter = (event: React.MouseEvent, idx: number) => {
        if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current)
        setShowDealsPopupIdx(idx)
        setPopupTarget(event.currentTarget as HTMLElement)
    }

    const handleQtyMouseLeave = () => {
        popupTimeoutRef.current = setTimeout(() => setShowDealsPopupIdx(null), 1000)
    }

    const handlePopupMouseEnter = () => {
        if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current)
    }

    const handlePopupMouseLeave = () => {
        popupTimeoutRef.current = setTimeout(() => setShowDealsPopupIdx(null), 1000)
    }

    return (
        <Table striped hover>
            <thead>
                <tr>
                    <th scope="col" style={colWidth(35)} className={styles.alignCenter}>
                        <Form.Check>
                            <Form.Check.Input
                                className={styles.checkboxLg}
                                checked={selectAll}
                                onChange={handleAllCheckboxChange}
                            />
                        </Form.Check>
                    </th>
                    {columns.map(column => {
                        switch (column.id) {
                            case 'groupId':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(54)}>
                                        <TableHeadCategorySearchParts />
                                    </th>
                                )
                            case 'customTitle':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(204)}>
                                        <TableHeadPartSearch oldSearchTerm={partName} />
                                    </th>
                                )
                            case 'markTitle':
                                return (
                                    <th
                                        key={column.id}
                                        scope="col"
                                        style={colWidth(100)}
                                        className={classNames({
                                            [styles.filtered]: carFilter,
                                        })}
                                    >
                                        {carFilter ? carFilter.markTitle : <TableHeadMarkSearchParts />}
                                    </th>
                                )
                            case 'modelTitle':
                                return (
                                    <th
                                        key={column.id}
                                        scope="col"
                                        style={colWidth(200)}
                                        className={classNames({
                                            [styles.filtered]: carFilter,
                                        })}
                                    >
                                        {carFilter ? carFilter.modelTitle : <TableHeadModelSearchParts />}
                                    </th>
                                )
                            case 'photo':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(35)}>
                                        Фото
                                    </th>
                                )
                            case 'vendorCode':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(135)}>
                                        Артикул
                                        {filterVendorCode ? (
                                            <ResetFilter filterName="filterVendorCode" />
                                        ) : (
                                            <ColumnSort
                                                iconType={SortIconType.NUMERIC}
                                                sortField={SortFieldParts.VENDOR_CODE}
                                                slice="partList"
                                                reverseSort
                                            />
                                        )}
                                    </th>
                                )
                            case 'partNumber':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(103)}>
                                        <TableHeadPartNumberSearchParts />
                                    </th>
                                )
                            case 'soldQuantity':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(35)}>
                                        Кол.
                                    </th>
                                )
                            case 'soldPrice':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(95)}>
                                        <TableHeadRangeFilter
                                            initialTableHead="Цена продажи"
                                            step={1000}
                                            leftInPixes={-35}
                                            initialFromVal={priceFrom}
                                            initialToVal={priceTo}
                                            widthInPixels={150}
                                            storeActionSetRange={storeDispatchTypes.setPriceRangeParts}
                                        />
                                    </th>
                                )
                            case 'soldAt':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(85)}>
                                        Дата продажи
                                        <ColumnSort
                                            iconType={SortIconType.NUMERIC}
                                            sortField={SortFieldParts.SOLD_AT}
                                            slice="partList"
                                            reverseSort
                                        />
                                    </th>
                                )
                            case 'createdAt':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(95)}>
                                        Дата создания
                                        <ColumnSort
                                            iconType={SortIconType.NUMERIC}
                                            sortField={SortFieldParts.CREATED_AT}
                                            slice="partList"
                                            reverseSort
                                        />
                                    </th>
                                )
                            case 'warehouseTitle':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(120)}>
                                        Склад
                                    </th>
                                )
                            case 'pricePurchase':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(100)}>
                                        Цена закупки
                                    </th>
                                )
                            case 'typeId':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(150)}>
                                        Тип запчасти
                                    </th>
                                )
                            case 'actions':
                                return (
                                    <th key={column.id} scope="col" style={colWidth(99)}>
                                        Действия
                                    </th>
                                )
                            default:
                                return null
                        }
                    })}
                </tr>
            </thead>
            {isLoading || !parts ? (
                <ListSkeleton rowCount={9} columnCount={columns.length + 1} marginVerticalRem={0.21} />
            ) : (
                <tbody>
                    {parts.map((part, index) => (
                        <tr key={part.id} className={styles.row}>
                            <td scope="row" className={styles.alignCenter}>
                                <Form.Check>
                                    <Form.Check.Input
                                        className={classNames(styles.checkboxLg, styles.pointer)}
                                        checked={
                                            selectedParts.some(selectedPart => selectedPart.id === part.id) || selectAll
                                        }
                                        onChange={() => handleCheckboxChange(part)}
                                        disabled={selectAll}
                                    />
                                </Form.Check>
                            </td>
                            {columns.map(column => {
                                switch (column.id) {
                                    case 'groupId':
                                        return (
                                            <td key={column.id} scope="row" onClick={() => openPartModal(part)}>
                                                <CategoryIcon categoryId={part.groupId} />
                                            </td>
                                        )
                                    case 'customTitle':
                                        return (
                                            <td key={column.id} scope="row" onClick={() => openPartModal(part)}>
                                                {part.customTitle || part.title}
                                            </td>
                                        )
                                    case 'markTitle':
                                        return (
                                            <td key={column.id} scope="row" onClick={() => openPartModal(part)}>
                                                {part.markTitle}
                                            </td>
                                        )
                                    case 'modelTitle':
                                        return (
                                            <td key={column.id} scope="row" onClick={() => openPartModal(part)}>
                                                {part.modelTitle}
                                            </td>
                                        )
                                    case 'photo':
                                        return (
                                            <td key={column.id} scope="row" className={styles.imageRow}>
                                                <CellThumbCarousel pictures={part.images} />
                                            </td>
                                        )
                                    case 'vendorCode':
                                        return (
                                            <td key={column.id} scope="row" className={styles.default}>
                                                <CopyButton
                                                    copyText={part.vendorCodePrefix + part.vendorCode}
                                                    overlayText="Артикул скопирован в буфер обмена"
                                                    isText
                                                />
                                            </td>
                                        )
                                    case 'partNumber':
                                        return (
                                            <td key={column.id} scope="row" className={styles.default}>
                                                <CopyButton
                                                    copyText={part.partNumber}
                                                    overlayText="ОЕМ скопирован в буфер обмена"
                                                    isText
                                                />
                                            </td>
                                        )
                                    case 'soldQuantity':
                                        return (
                                            <td key={column.id} scope="row">
                                                <span
                                                    className={styles.soldQuantity}
                                                    onMouseEnter={event => handleQtyMouseEnter(event, index)}
                                                    onMouseLeave={handleQtyMouseLeave}
                                                >
                                                    {partSoldQtyCount(part) || 0}
                                                </span>
                                                <Overlay
                                                    show={showDealsPopupIdx === index}
                                                    target={popupTarget}
                                                    placement="top"
                                                    containerPadding={10}
                                                    rootClose={false}
                                                >
                                                    <Popover
                                                        id={`sold-deals-popover-${index}`}
                                                        className={styles.dealsPopover}
                                                        onMouseEnter={handlePopupMouseEnter}
                                                        onMouseLeave={handlePopupMouseLeave}
                                                    >
                                                        <Popover.Body>
                                                            <b>
                                                                Сделки продажи (
                                                                {(part.extraData?.soldDealsArr || []).length}
                                                                ):
                                                            </b>
                                                            {(part.extraData?.soldDealsArr || []).length === 0 ? (
                                                                <div>Нет данных о продажах</div>
                                                            ) : (
                                                                (part.extraData?.soldDealsArr || []).map(
                                                                    (deal, idx) => (
                                                                        <div
                                                                            key={deal.dealId || idx}
                                                                            className={styles.dealItem}
                                                                        >
                                                                            <span className={styles.dealDate}>
                                                                                {deal.soldAt
                                                                                    ? new Date(
                                                                                          deal.soldAt as string,
                                                                                      ).toLocaleDateString('ru-RU')
                                                                                    : ''}
                                                                            </span>
                                                                            {deal.dealId && (
                                                                                <>
                                                                                    {' | '}
                                                                                    <Link
                                                                                        to={`/admin/deals/edit/${deal.dealId}`}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                    >
                                                                                        Сделка
                                                                                    </Link>
                                                                                </>
                                                                            )}
                                                                            {deal.dealStatus && ` | ${deal.dealStatus}`}
                                                                            {deal.marketplace &&
                                                                                ` | ${deal.marketplace}`}
                                                                            {deal.price &&
                                                                                ` | ${deal.price.toLocaleString('ru-RU')}₽`}
                                                                            {deal.qty && ` | ${deal.qty} шт.`}
                                                                        </div>
                                                                    ),
                                                                )
                                                            )}
                                                        </Popover.Body>
                                                    </Popover>
                                                </Overlay>
                                            </td>
                                        )
                                    case 'soldPrice':
                                        return (
                                            <td
                                                key={column.id}
                                                scope="row"
                                                className={styles.alignRight}
                                                onClick={() => openPartModal(part)}
                                            >
                                                {partSoldQtyAmountCost(part) || 0}
                                            </td>
                                        )
                                    case 'soldAt':
                                        return (
                                            <td key={column.id} scope="row" className={styles.alignRight}>
                                                <div>
                                                    <Badge
                                                        text={formatDate(part.soldAt)}
                                                        color="#ddd"
                                                        fontColor="#555"
                                                    />
                                                </div>
                                                {+countDaysInStock(`${part.soldAt}`) > 10 && (
                                                    <div>
                                                        <Badge
                                                            text={`${countDaysInStock(`${part.soldAt}`)} ${num_word(
                                                                +countDaysInStock(`${part.soldAt}`),
                                                                ['день', 'дня', 'дней'],
                                                            )}`}
                                                            color="#eee"
                                                            fontColor="#777"
                                                        />
                                                    </div>
                                                )}
                                            </td>
                                        )
                                    case 'createdAt':
                                        return (
                                            <td key={column.id} scope="row" className={styles.alignRight}>
                                                <Badge
                                                    text={formatDate(part.createdAt)}
                                                    color="#ddd"
                                                    fontColor="#555"
                                                />
                                            </td>
                                        )
                                    case 'warehouseTitle':
                                        return (
                                            <td key={column.id} scope="row" onClick={() => openPartModal(part)}>
                                                {part.warehouseData?.title || '-'}
                                            </td>
                                        )
                                    case 'pricePurchase':
                                        return (
                                            <td key={column.id} scope="row" className={styles.alignRight}>
                                                {typeof part.extraData?.pricePurchase === 'number'
                                                    ? part.extraData.pricePurchase.toLocaleString('ru-RU')
                                                    : '-'}
                                            </td>
                                        )
                                    case 'typeId':
                                        return (
                                            <td key={column.id} scope="row" onClick={() => openPartModal(part)}>
                                                {part.title}
                                            </td>
                                        )
                                    case 'actions':
                                        return (
                                            <td key={column.id} scope="row" className={styles.default}>
                                                <CellActionsParts
                                                    part={part}
                                                    isSafari={browserName === 'Safari'}
                                                    isSoldPartList
                                                />
                                            </td>
                                        )
                                    default:
                                        return null
                                }
                            })}
                        </tr>
                    ))}
                </tbody>
            )}
        </Table>
    )
}

export default TableSoldPartList
